import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import DocLayout from '../components/docs/DocLayout'
import CopyButton from '../components/docs/CopyButton'
import Icon from '../components/docs/Icon'
import { findComponent } from '../content/components'
import { findTemplate, pageTemplates } from '../content/templates'
import Pager from '../components/docs/Pager'
import NotFoundPage from './NotFoundPage'
import { previewPath, type TemplateId } from '@/templates/types'
import Cased from '@/components/ui/Cased'
import { zip } from '@/components/inspector/zip'

const widths = [
  { id: 'desktop', label: 'Desktop', px: 1280 },
  { id: 'tablet', label: 'Tablet', px: 768 },
  { id: 'phone', label: 'Phone', px: 375 },
] as const

const FRAME_HEIGHT = 760

const shared = ['TemplateChrome.tsx', 'types.ts']

/** Reads a generated file under /mds/templates, or an empty string where it cannot. */
const read = (file: string) =>
  fetch(`/mds/templates/${file}`)
    .then((response) => (response.ok ? response.text() : ''))
    .catch(() => '')

export default function TemplateDetailPage() {
  const { slug } = useParams()
  const template = findTemplate(slug ?? '')
  const [width, setWidth] = useState<(typeof widths)[number]['id']>('desktop')
  const [scale, setScale] = useState(1)
  const [loaded, setLoaded] = useState({ id: '', html: '', react: '' })
  const stage = useRef<HTMLDivElement>(null)
  const px = widths.find((item) => item.id === width)!.px

  // A frame wider than the column is scaled down to fit, like a device preview.
  useLayoutEffect(() => {
    const element = stage.current
    if (!element) return
    const fit = () => setScale(Math.min(1, element.clientWidth / px))
    fit()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(fit)
    observer.observe(element)
    return () => observer.disconnect()
  }, [px])

  useEffect(() => {
    if (!template) return
    void Promise.all([read(`${template.id}.html`), read(`react/${template.source}`)]).then(([html, react]) =>
      setLoaded({ id: template.id, html, react }),
    )
  }, [template])

  if (!template) return <NotFoundPage />
  // The gallery's order, so Previous and Next walk every template.
  const index = pageTemplates.findIndex((item) => item.id === template.id)
  const previous = index > 0 ? pageTemplates[index - 1] : undefined
  const next = index < pageTemplates.length - 1 ? pageTemplates[index + 1] : undefined
  const preview = previewPath(template.id as TemplateId)
  const code = loaded.id === template.id ? loaded : { html: '', react: '' }

  // The template's React file with the two shared files it imports, as one zip.
  const downloadReact = async () => {
    const files = Object.fromEntries(
      await Promise.all([template.source, ...shared].map(async (file) => [`${template.id}/${file}`, await read(`react/${file}`)])),
    )
    const url = URL.createObjectURL(new Blob([zip(files)], { type: 'application/zip' }))
    const link = Object.assign(document.createElement('a'), { href: url, download: `mds-${template.id}-react.zip` })
    link.click()
    URL.revokeObjectURL(url)
  }

  return (
    <DocLayout section={{ label: 'Templates', to: '/templates' }} eyebrow="Templates" title={template.name} intro={template.summary} wide>
      <section className="doc-section">
        <div className="tpl-toolbar">
          <div className="mds-segmented" role="group" aria-label="Preview width">
            {widths.map((item) => (
              <button
                key={item.id}
                type="button"
                className="mds-segmented__option"
                aria-pressed={width === item.id}
                onClick={() => setWidth(item.id)}
              >
                {item.label}
              </button>
            ))}
          </div>
          <a className="mds-button mds-button--secondary mds-button--sm" href={preview} target="_blank" rel="noopener">
            Open full page
            <Icon name="arrow" size={14} />
            <span className="mds-visually-hidden"> (opens in a new tab)</span>
          </a>
        </div>
        {/* The frame is as wide as the device it shows, centered in the column. */}
        <div ref={stage}>
          <div className="tpl-stage" style={{ width: px * scale, height: FRAME_HEIGHT * scale }}>
            <iframe
              key={template.id}
              className="tpl-frame"
              src={preview}
              title={`${template.name} template preview`}
              width={px}
              height={FRAME_HEIGHT}
              style={{ transform: `scale(${scale})` }}
            />
          </div>
        </div>
        <p className="mds-hint">
          The preview uses your chosen mission and theme. Showing {px} pixels wide
          {scale < 1 ? `, at ${Math.round(scale * 100)}% to fit` : ''}.
        </p>
      </section>
      <div className="pat-guidance">
        <section className="doc-section">
          <h2>
            <Cased>When to use</Cased>
          </h2>
          <ul className="pat-checks">
            {template.useWhen.map((item) => (
              <li key={item}>
                <Icon name="check" size={17} />
                {item}
              </li>
            ))}
          </ul>
        </section>
        <section className="doc-section">
          <h2>
            <Cased>Anatomy</Cased>
          </h2>
          <ol className="pat-anatomy">
            {template.anatomy.map((item, index) => (
              <li key={item}>
                <span>{String(index + 1).padStart(2, '0')}</span>
                {item}
              </li>
            ))}
          </ol>
        </section>
      </div>
      <section className="doc-section">
        <h2>
          <Cased>Accessibility</Cased>
        </h2>
        <ul className="pat-checks">
          {template.accessibility.map((item) => (
            <li key={item}>
              <Icon name="check" size={17} />
              {item}
            </li>
          ))}
        </ul>
      </section>
      <section className="doc-section">
        <h2>
          <Cased>Design files</Cased>
        </h2>
        <p>
          The template as editable layers for Figma, Illustrator and Canva, and as a PDF, in the default theme. The{' '}
          <Link className="mds-link" to="/design-tools">
            Figma plugin
          </Link>{' '}
          draws it with every color bound to a variable.
        </p>
        <div className="mds-cluster">
          <a className="mds-button mds-button--secondary mds-button--sm" href={`/mds/design/svg/templates/${template.id}.svg`} download>
            <Icon name="download" size={16} />
            SVG
          </a>
          <a className="mds-button mds-button--secondary mds-button--sm" href={`/mds/design/pdf/templates/${template.id}.pdf`} download>
            <Icon name="download" size={16} />
            PDF
          </a>
        </div>
      </section>
      <section className="doc-section">
        <h2>
          <Cased>HTML</Cased>
        </h2>
        <p>
          Plain HTML with the published <code>mds-*</code> classes. It links <code>mds.css</code> and a small script for the
          banner toggle; everything else is native.
        </p>
        <div className="mds-cluster">
          {code.html && <CopyButton text={code.html} label="Copy HTML" />}
          <a className="mds-button mds-button--secondary mds-button--sm" href={`/mds/templates/${template.id}.html`} download>
            <Icon name="download" size={16} />
            Download HTML
          </a>
        </div>
      </section>
      <section className="doc-section">
        <h2>
          <Cased>React</Cased>
        </h2>
        <p>
          The template takes a mission and a link function, imports its pieces from <code>missiondesign-react</code>, and
          shares a frame with the other templates.
        </p>
        <div className="mds-cluster">
          {code.react && <CopyButton text={code.react} label="Copy React source" />}
          <button type="button" className="mds-button mds-button--secondary mds-button--sm" onClick={() => void downloadReact()}>
            <Icon name="download" size={16} />
            Download React files
          </button>
        </div>
        <details className="doc-details">
          <summary>Show the source</summary>
          <pre className="mds-code">
            <code>{code.react || 'Loading…'}</code>
          </pre>
        </details>
      </section>
      <section className="doc-section">
        <h2>
          <Cased>Components used</Cased>
        </h2>
        <ul className="tpl-related">
          {template.components.map((id) => {
            const component = findComponent(id)
            return component ? (
              <li key={id}>
                <Link className="mds-link" to={`/components/${id}`}>
                  {component.name}
                </Link>
              </li>
            ) : null
          })}
        </ul>
      </section>
      <Pager
        label="Templates"
        previous={previous && { to: `/templates/${previous.id}`, title: previous.name }}
        next={next && { to: `/templates/${next.id}`, title: next.name }}
      />
    </DocLayout>
  )
}
